import { useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function RegisterPage() {
  const [form, setForm] = useState({
    name: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_password: '',
    password_confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (form.admin_password !== form.password_confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (form.admin_password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          admin_first_name: form.admin_first_name.trim(),
          admin_last_name: form.admin_last_name.trim(),
          admin_email: form.admin_email.trim(),
          admin_password: form.admin_password,
        }),
        cache: 'no-store',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setResendMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.admin_email.trim() }),
        cache: 'no-store',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setResendMsg('Bestätigungslink wurde erneut gesendet.');
    } catch (err) {
      setResendMsg(`Fehler: ${err.message}`);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'var(--color-bg-content)' }}>
      <div className="card shadow-sm" style={{ width: '100%', maxWidth: 460 }}>
        <div className="card-body p-4">
          <div className="text-center mb-4">
            <i className="bi bi-lightning-charge-fill text-warning fs-1" />
            <h4 className="mt-2 fw-bold">MK Workout</h4>
            <p className="text-muted small">Neues Konto erstellen</p>
          </div>

          {success ? (
            <div className="text-center">
              <div className="alert alert-success">
                <i className="bi bi-envelope-check me-2" />
                Registrierung erfolgreich! Wir haben dir eine Bestätigungs-E-Mail gesendet.
              </div>
              <p className="text-muted small">
                Bitte prüfe dein Postfach (und den Spam-Ordner) und klicke auf den Bestätigungslink.
              </p>
              {resendMsg && (
                <div className="alert alert-info py-2 small mt-2">{resendMsg}</div>
              )}
              <button
                className="btn btn-outline-secondary btn-sm mt-2 me-2"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Senden...</>
                ) : (
                  <><i className="bi bi-arrow-repeat me-1" />Erneut senden</>
                )}
              </button>
              <Link to="/login" className="btn btn-outline-secondary btn-sm mt-2">
                <i className="bi bi-arrow-left me-1" />
                Zur Anmeldung
              </Link>
            </div>
          ) : (
            <>
              {error && <div className="alert alert-danger py-2 small">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Organisationsname</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    required
                    autoFocus
                    placeholder="z.B. Fitness Studio Berlin"
                  />
                </div>

                <div className="row mb-3">
                  <div className="col">
                    <label className="form-label">Vorname</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.admin_first_name}
                      onChange={(e) => update('admin_first_name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">Nachname</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.admin_last_name}
                      onChange={(e) => update('admin_last_name', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">E-Mail</label>
                  <input
                    type="email"
                    className="form-control"
                    value={form.admin_email}
                    onChange={(e) => update('admin_email', e.target.value)}
                    required
                    placeholder="deine@email.de"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Passwort</label>
                  <input
                    type="password"
                    className="form-control"
                    value={form.admin_password}
                    onChange={(e) => update('admin_password', e.target.value)}
                    required
                    minLength={8}
                    placeholder="Mindestens 8 Zeichen"
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label">Passwort bestätigen</label>
                  <input
                    type="password"
                    className="form-control"
                    value={form.password_confirm}
                    onChange={(e) => update('password_confirm', e.target.value)}
                    required
                    minLength={8}
                  />
                </div>

                <button type="submit" className="btn btn-primary w-100 mb-3" disabled={loading}>
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Registrieren...</>
                  ) : (
                    <><i className="bi bi-person-plus me-1" />Registrieren</>
                  )}
                </button>

                <div className="text-center">
                  <span className="text-muted small">Bereits ein Konto? </span>
                  <Link to="/login" className="small">Anmelden</Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
